import { useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import useToast from 'hooks/useToast'
import Heading from './Heading'
import Toggle from 'components/core/Toggle'
import ShareCodeInput from './ShareCodeInput'
import api from 'services/api'

const ExportForVeo = () => {
  const toast = useToast()
  const [sourceId, setSourceId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloadingAudio, setIsDownloadingAudio] = useState(false)
  const [transcript, setTranscript] = useState(null)
  const [trim, setTrim] = useState(true)

  const handleExport = useCallback(async () => {
    if (!sourceId) return

    try {
      setIsLoading(true)

      const transcriptData = await api.reclips.getReclipRawTranscript(
        sourceId,
        trim
      )
      // Fetch and display transcript
      setTranscript(transcriptData)

      toast({ message: 'Transcript loaded' })
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      setIsLoading(false)
    }
  }, [sourceId, trim, toast])

  const handleDownloadAudio = useCallback(async () => {
    if (!sourceId) return

    let blobUrl
    try {
      setIsDownloadingAudio(true)

      const blob = await api.reclips.getReclipAudio(sourceId, trim)

      // Create a blob URL from the returned blob data
      blobUrl = URL.createObjectURL(blob)
      // Create a temporary anchor element with the blob URL
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${sourceId}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({ message: 'Audio download started' })
    } catch (e) {
      toast({ message: e.message, severity: 'error' })
    } finally {
      if (blobUrl) {
        // Cleanup blob URL
        URL.revokeObjectURL(blobUrl)
      }
      setIsDownloadingAudio(false)
    }
  }, [sourceId, trim, toast])

  const handleCopyTranscript = async () => {
    if (transcript) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(transcript, null, 2))
        toast({ message: 'Transcript copied to clipboard' })
      } catch (err) {
        console.error('Failed to copy transcript: ', err)
        toast({
          message: 'Failed to copy transcript to clipboard.',
          severity: 'error',
        })
      }
    }
  }

  return (
    <Box>
      <Heading text="1. Enter Source ID" />
      <ShareCodeInput
        shareLink={sourceId}
        onChange={setSourceId}
        placeholder="e.g. 123456AA-BC78-9DE0-1F2G-HI3456789JKL"
      />

      <Heading text="2. Export for Veo" />
      <Toggle
        isChecked={trim}
        setIsChecked={setTrim}
        label="Trim audio/transcript"
      />
      <LoadingButton
        variant="contained"
        disabled={!sourceId}
        onClick={handleExport}
        loading={isLoading}
        sx={{ mb: 2, mt: 2 }}
      >
        Get Transcript
      </LoadingButton>

      <LoadingButton
        variant="contained"
        disabled={!sourceId}
        onClick={handleDownloadAudio}
        loading={isDownloadingAudio}
        sx={{ display: 'block' }}
      >
        Download Audio
      </LoadingButton>

      {transcript && (
        <Box sx={{ mt: 4 }}>
          <Heading text="3. Raw Transcript JSON" />
          <Button
            variant="contained"
            onClick={handleCopyTranscript}
            sx={{ mb: 2 }}
          >
            Copy Transcript
          </Button>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              maxHeight: 400,
              overflow: 'auto',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            {JSON.stringify(transcript, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ExportForVeo
